import cn from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";
import { Button, Col, Container, Row } from "reactstrap";
import { ADVERTISEMENT_LOADING } from "../../constants/loading.constant";
import { useAdvertisementCore } from "../../cores/advertisements";
import { useFacebookAdsCore } from "../../cores/facebookAds";
import { useMeCore } from "../../cores/me";
import { useRouterCore } from "../../cores/router";
import { useIntl } from "react-intl";
import AdsFlatTable from "../component/advertisements/AdsFlatTable";
import AdsetsFlatTable from "../component/advertisements/AdsetsFlatTable";
import CampaingsTable from "../component/advertisements/CampaignsTable";
import { NoData } from "../component/NoData";
import { PanelWembii } from "../component/PanelWembii";
import { UpdateLoadingCard } from "../component/UpdateLoadingCard";
import Content from "../layout/Content";
import { Header2 } from "../layout/Header2";
import Layout from "../layout/Layout";
import { useGlobalCore } from "../../cores/globals";
import "./FacebookCampaigns.scss";

type AdsTab = "campaigns" | "adsets" | "ads";

export default function FacebookCampaigns() {
  const intl = useIntl();
  const [adsTab, setAdsTab] = useState<AdsTab>("campaigns");
  const { goToCreateCampaign, goToSocial } = useRouterCore();
  const { company, companyPlan, companyRole, isAdmin, isReseller } =
    useMeCore();
  const { campaigns, getCampaigns, getMinBudget } = useAdvertisementCore();
  const { isLoading, isDarkMode } = useGlobalCore();

  const { settings } = useFacebookAdsCore();

  const loadingCampaigns = isLoading(ADVERTISEMENT_LOADING);

  useEffect(() => {
    if (
      !companyPlan?.rules.allowFacebookAdvertisements ||
      (!companyRole?.ads &&
        !companyRole?.owner &&
        !companyRole?.admin &&
        !companyRole?.agent &&
        !isAdmin &&
        !isReseller)
    )
      goToSocial();
  }, [companyPlan, companyRole, isAdmin, isReseller, goToSocial]);

  useEffect(() => {
    if (!company?.id || !settings?.accountId) return;
    getCampaigns(company.id, settings.accountId);
    getMinBudget(company.id, settings.accountId, settings.currency);
  }, [company, getCampaigns, getMinBudget, settings]);

  return (
    <Layout>
      <Header2
        title={intl.formatMessage({ id: "app.advertisements.title" })}
        rightChildren={
          <>
            {!loadingCampaigns && settings?.accountId && (
              <Button
                type="button"
                className="fb-campaigns-ai__cta"
                onClick={goToCreateCampaign}
              >
                {intl.formatMessage({
                  id: "app.advertisements.createCampaing",
                })}
              </Button>
            )}
          </>
        }
      />
      <Content>
        <UpdateLoadingCard
          loadingId={ADVERTISEMENT_LOADING}
          text={intl.formatMessage({
            id: "app.advertisements.updatingCampaigns",
          })}
        />
        <Container fluid className="px-md-3">
          <div
            className={cn("fb-campaigns-ai", {
              "fb-campaigns-ai--dark": isDarkMode,
            })}
          >
            {loadingCampaigns ? null : campaigns.length > 0 ? (
              <Row>
                <Col xs="12">
                  <PanelWembii
                    shadow
                    className="mb-4 p-3 p-md-4 fb-campaigns-ai__panel fb-campaigns-ai__panel--with-tabs fb-campaigns-ai__panel--fill"
                  >
                    <div className="fb-campaigns-ai__tabs-bar">
                      <nav
                        className="fb-campaigns-ai__tabs"
                        aria-label={intl.formatMessage({
                          id: "app.advertisements.tabs.navAria",
                        })}
                      >
                        {(
                          [
                            "campaigns",
                            "adsets",
                            "ads",
                          ] as const
                        ).map((key) => (
                          <button
                            key={key}
                            type="button"
                            className={cn("fb-campaigns-ai__tab", {
                              "fb-campaigns-ai__tab--active": adsTab === key,
                            })}
                            onClick={() => setAdsTab(key)}
                          >
                            {intl.formatMessage({
                              id: `app.advertisements.tabs.${key}`,
                            })}
                          </button>
                        ))}
                        <button
                          type="button"
                          className="fb-campaigns-ai__tab fb-campaigns-ai__tab--disabled"
                          disabled
                          title={intl.formatMessage({
                            id: "app.advertisements.tabs.comingSoon",
                          })}
                        >
                          {intl.formatMessage({
                            id: "app.advertisements.tabs.keywords",
                          })}{" "}
                          <FontAwesomeIcon
                            icon={["fab", "google"]}
                            className="fb-campaigns-ai__tab-icon"
                          />
                        </button>
                        <button
                          type="button"
                          className="fb-campaigns-ai__tab fb-campaigns-ai__tab--disabled"
                          disabled
                          title={intl.formatMessage({
                            id: "app.advertisements.tabs.comingSoon",
                          })}
                        >
                          {intl.formatMessage({
                            id: "app.advertisements.tabs.recommendations",
                          })}{" "}
                          <FontAwesomeIcon
                            icon={["fab", "google"]}
                            className="fb-campaigns-ai__tab-icon"
                          />
                        </button>
                      </nav>
                      <div className="fb-campaigns-ai__tabs-actions">
                        <FontAwesomeIcon
                          icon={["fab", "meta"]}
                          className="fb-campaigns-ai__tabs-meta"
                          title="Meta"
                        />
                        <button
                          type="button"
                          className="fb-campaigns-ai__tab-add"
                          onClick={goToCreateCampaign}
                          title={intl.formatMessage({
                            id: "app.advertisements.createCampaing",
                          })}
                          aria-label={intl.formatMessage({
                            id: "app.advertisements.createCampaing",
                          })}
                        >
                          <FontAwesomeIcon icon={["fas", "plus"]} />
                        </button>
                      </div>
                    </div>
                    <div className="fb-campaigns-ai__tab-panel">
                      {adsTab === "campaigns" && (
                        <CampaingsTable showNestedHierarchy={false} />
                      )}
                      {adsTab === "adsets" && (
                        <AdsetsFlatTable campaigns={campaigns} />
                      )}
                      {adsTab === "ads" && (
                        <AdsFlatTable campaigns={campaigns} />
                      )}
                    </div>
                  </PanelWembii>
                </Col>
              </Row>
            ) : (
              <NoData
                image="generic6"
                text={intl.formatMessage({
                  id: "app.advertisements.notHaveCampaign",
                })}
                actionLabel={intl.formatMessage({
                  id: "app.advertisements.createNewCampaing",
                })}
                action={goToCreateCampaign}
                horizontal
              />
            )}
          </div>
        </Container>
      </Content>
    </Layout>
  );
}
